import { useState } from "react";
import { Camera, X, ImagePlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import EvidenceUploader from "@/components/EvidenceUploader";
import { toast } from "sonner";

/**
 * ServiceEvidences — bloque dedicado para evidencias adheridas al servicio.
 * - Muestra galería de fotos existentes (service.evidences[])
 * - Permite agregar nuevas (POST /services/:id/evidences)
 * - Permite eliminar individualmente (DELETE /services/:id/evidences/:evidence_id)
 *
 * Props:
 *   service: { id, evidences: [{id, data_url, caption, timestamp, uploaded_by_name}] }
 *   readOnly: bool (true para portal corporativo donde solo se ve la galería)
 *   onChange: () => void  // callback para refrescar el detalle del servicio
 */
export default function ServiceEvidences({ service, readOnly = false, onChange }) {
    const [staged, setStaged] = useState([]);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);

    const existing = service.evidences || [];

    const submitStaged = async () => {
        if (staged.length === 0) {
            toast.warning("Sin evidencias nuevas para subir");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/services/${service.id}/evidences`, { evidences: staged });
            toast.success(`${staged.length} evidencia(s) agregada(s)`);
            setStaged([]);
            onChange?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al subir evidencias");
        } finally {
            setSaving(false);
        }
    };

    const removeOne = async (evidenceId) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("¿Eliminar esta evidencia? Esta acción no se puede deshacer.")) return;
        try {
            await api.delete(`/services/${service.id}/evidences/${evidenceId}`);
            toast.success("Evidencia eliminada");
            onChange?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al eliminar");
        }
    };

    return (
        <div className="fm-card p-5" data-testid="service-evidences">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display font-bold text-base flex items-center gap-2">
                    <Camera className="w-4 h-4" strokeWidth={1.5} /> Evidencias del servicio
                </h3>
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">
                    {existing.length} archivo(s)
                </span>
            </div>

            {/* Galería existente */}
            {existing.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-sm py-8 text-center text-xs text-slate-400">
                    Sin evidencias adheridas.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="evidences-gallery">
                    {existing.map((ev, i) => (
                        <div key={ev.id || i} className="border border-slate-200 rounded-sm overflow-hidden bg-white group relative" data-testid={`evidence-${i}`}>
                            <button
                                type="button"
                                onClick={() => setPreview(ev)}
                                className="block w-full text-left"
                                aria-label="Ampliar evidencia"
                            >
                                <img
                                    src={ev.data_url}
                                    alt={ev.caption || `Evidencia ${i + 1}`}
                                    className="w-full h-32 object-cover hover:opacity-90 transition-opacity"
                                />
                            </button>
                            {!readOnly && ev.id && (
                                <button
                                    type="button"
                                    onClick={() => removeOne(ev.id)}
                                    className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-700 text-white rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`evidence-remove-${i}`}
                                    aria-label="Eliminar evidencia"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                            <div className="px-2 py-1.5 border-t border-slate-200">
                                <div className="text-xs truncate">{ev.caption || <span className="text-slate-400 italic">Sin descripción</span>}</div>
                                <div className="text-[10px] text-slate-400 font-mono">
                                    {ev.timestamp ? new Date(ev.timestamp).toLocaleString().slice(0, 16) : ""}
                                    {ev.uploaded_by_name && <> · {ev.uploaded_by_name}</>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Uploader (oculto en modo readOnly) */}
            {!readOnly && (
                <div className="mt-5 pt-5 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="font-display font-bold text-sm flex items-center gap-2">
                            <ImagePlus className="w-4 h-4" /> Agregar evidencias
                        </h4>
                        {staged.length > 0 && (
                            <button
                                type="button"
                                onClick={submitStaged}
                                disabled={saving}
                                data-testid="upload-staged-btn"
                                className="fm-btn-accent text-xs"
                            >
                                {saving ? "Subiendo..." : `Subir ${staged.length} archivo(s)`}
                            </button>
                        )}
                    </div>
                    <EvidenceUploader value={staged} onChange={setStaged} testId="service-evidence-uploader" />
                </div>
            )}

            {/* Modal preview */}
            {preview && (
                <div
                    className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setPreview(null)}
                    data-testid="evidence-preview-modal"
                >
                    <div className="max-w-4xl w-full bg-white rounded-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-3 border-b border-slate-200">
                            <div>
                                <div className="font-display font-bold text-sm">{preview.caption || "Evidencia"}</div>
                                <div className="text-[10px] text-slate-500 font-mono">
                                    {preview.timestamp ? new Date(preview.timestamp).toLocaleString() : ""}
                                    {preview.uploaded_by_name && <> · subida por {preview.uploaded_by_name}</>}
                                </div>
                            </div>
                            <button onClick={() => setPreview(null)} aria-label="Cerrar"><X className="w-5 h-5" /></button>
                        </div>
                        <img src={preview.data_url} alt={preview.caption || "Evidencia"} className="max-h-[80vh] w-full object-contain bg-slate-100" />
                    </div>
                </div>
            )}
        </div>
    );
}
